import { Fragment, useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  ChecklistIcon, GearIcon, GraphIcon, PackageIcon, ProjectIcon,
  PeopleIcon, QuestionIcon, ShieldCheckIcon, SlidersIcon, TagIcon
} from "@primer/octicons-react";
import { AppShell } from "./components/AppShell/AppShell.jsx";
import { QuickSyncDialog } from "./components/ProfileManager/QuickSyncDialog.jsx";
import { assetHref } from "./components/AssetIdentity/AssetIdentity.jsx";
import { policyDisplayTitle, policyHref } from "./components/PolicyIdentity/PolicyIdentity.jsx";
import { PortfolioValueProvider } from "./components/PortfolioValue/PortfolioValue.jsx";
import { propertyHref } from "./components/PropertyIdentity/PropertyIdentity.jsx";
import { propertyValueHref } from "./components/PropertyValueIdentity/PropertyValueIdentity.jsx";
import { PlaceholderPage } from "./pages/PlaceholderPage.jsx";
import { AssetPage } from "./pages/AssetPage.jsx";
import { AssetsPage } from "./pages/AssetsPage.jsx";
import { AllocationPage } from "./pages/AllocationPage.jsx";
import { BrowserPage } from "./pages/BrowserPage.jsx";
import { CompliancePage } from "./pages/CompliancePage.jsx";
import { HelpPage } from "./pages/HelpPage.jsx";
import { PropertiesPage } from "./pages/PropertiesPage.jsx";
import { PropertyPage } from "./pages/PropertyPage.jsx";
import { PropertyValuePage } from "./pages/PropertyValuePage.jsx";
import { PolicyPage } from "./pages/PolicyPage.jsx";
import { PoliciesPage } from "./pages/PoliciesPage.jsx";
import { ProfilesPage } from "./pages/ProfilesPage.jsx";
import { ReportBuilderPage } from "./pages/ReportBuilderPage.jsx";
import { SharedProfilePage } from "./pages/SharedProfilePage.jsx";
import { StylesheetPage } from "./pages/StylesheetPage.jsx";
import { SettingsPage } from "./pages/SettingsPage.jsx";
import { ShareTestPage } from "./pages/ShareTestPage.jsx";
import { createAppShortcutBindings } from "./lib/shortcuts/appShortcutBindings.js";
import { sharedRouteParts } from "./lib/sharedProfileLink.js";
import { useAppShortcuts } from "./lib/shortcuts/useAppShortcuts.js";
import { ApplicationDocumentProvider, useApplicationDocument } from "./storage/useApplicationDocument.js";
import { sharedProfileToken } from "./storage/profileSharingPreferences.js";

const pages = [
  { key: "compliance", label: "Compliance", group: "Portfolio", scope: "profile", icon: ChecklistIcon },
  { key: "browser", label: "Browser", group: "Portfolio", scope: "profile", icon: ProjectIcon },
  { key: "allocation", label: "Allocation", group: "Portfolio", scope: "profile", icon: GraphIcon },
  { key: "properties", label: "Properties", group: "Configuration", scope: "profile", icon: TagIcon },
  { key: "assets", label: "Assets", group: "Configuration", scope: "profile", icon: PackageIcon },
  { key: "policies", label: "Policies", group: "Configuration", scope: "profile", icon: ShieldCheckIcon },
  { key: "profiles", label: "Profiles", group: "Application", scope: "application", icon: PeopleIcon },
  { key: "reports", label: "Report", group: "Application", scope: "application", navigation: false, icon: ProjectIcon },
  { key: "settings", label: "Settings", group: "Application", scope: "application", icon: GearIcon },
  { key: "help", label: "Help", group: "Application", scope: "application", icon: QuestionIcon },
  { key: "stylesheet", label: "Stylesheet", group: "Development", scope: "application", navigation: false, icon: SlidersIcon },
  { key: "share-test", label: "Share test", group: "Development", scope: "application", navigation: false, icon: SlidersIcon },
  { key: "shared", label: "Share", group: "Application", scope: "application", navigation: false, icon: PeopleIcon }
];

function pageFromHash() {
  const [pageKey, ...path] = window.location.hash.replace(/^#\/?/, "").split("/");
  const page = pages.find((item) => item.key === pageKey) ?? pages[0];
  return { ...page, path };
}

function decodedPathSegment(value) {
  if (!value) return undefined;
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function navigateToPage(pageKey) {
  window.location.hash = `#/${pageKey}`;
}

function shellPageForRoute(currentPage) {
  if (currentPage.key !== "shared") return currentPage;
  const route = sharedRouteParts(currentPage.path[0]);
  return route.token && route.mode === "sync"
    ? { ...pages.find((page) => page.key === "profiles"), path: [] }
    : currentPage;
}

function profileById(profiles, profileId) {
  return profiles.find((profile) => profile.id === profileId) ?? null;
}

function storedSyncConnection(profileId) {
  return { profileId, token: profileId ? sharedProfileToken(profileId) : null };
}

function syncTokenForProfile(connection, profileId) {
  if (connection && connection.profileId === profileId) return connection.token;
  return profileId ? sharedProfileToken(profileId) : null;
}

function quickSyncAvailable(syncToken, openProfileId) {
  return Boolean(syncToken || openProfileId);
}

function sortedBreadcrumbOptions(records, optionForRecord) {
  return records.map(optionForRecord).sort((left, right) => left.label.localeCompare(right.label, undefined, { sensitivity: "base" }));
}

function propertyBreadcrumbs(root, document, propertyId, valueId) {
  const property = document.properties.find((item) => item.id === propertyId);
  if (valueId) {
    const value = property?.values.find((item) => item.id === valueId);
    return [
      { ...root, href: "#/properties" },
      property ? { href: propertyHref(property.id), label: property.name } : { label: propertyId },
      value ? {
        id: value.id,
        label: value.name,
        options: property.values.map((item, index) => ({
          description: index === 0 ? "Default" : undefined,
          href: propertyValueHref(property.id, item.id),
          id: item.id,
          label: item.name
        }))
      } : { label: valueId }
    ];
  }
  return [
    { ...root, href: "#/properties" },
    property ? {
      id: property.id,
      label: property.name,
      options: sortedBreadcrumbOptions(document.properties, (item) => ({ href: propertyHref(item.id), id: item.id, label: item.name }))
    } : { label: propertyId === "new" ? "New property" : propertyId }
  ];
}

function breadcrumbsForPage(currentPage, document) {
  const root = { label: currentPage.label };
  const recordId = decodedPathSegment(currentPage.path[0]);
  if (!recordId || !document) return [root];

  if (currentPage.key === "assets") {
    const asset = document.assets.find((item) => item.id.toLocaleLowerCase() === recordId.toLocaleLowerCase());
    return [
      { ...root, href: "#/assets" },
      asset ? {
        id: asset.id,
        label: asset.name || asset.id,
        options: sortedBreadcrumbOptions(document.assets, (item) => ({
          description: item.name && item.name !== item.id ? item.id : undefined,
          href: assetHref(item.id),
          id: item.id,
          label: item.name || item.id
        }))
      } : { label: recordId === "new" ? "New asset" : recordId }
    ];
  }

  if (currentPage.key === "properties") {
    const valueId = decodedPathSegment(currentPage.path[1]);
    return propertyBreadcrumbs(root, document, recordId, valueId);
  }

  if (currentPage.key === "policies") {
    const policy = (document.policies ?? []).find((item) => item.id === recordId);
    return [
      { ...root, href: "#/policies" },
      policy ? {
        id: policy.id,
        label: policyDisplayTitle(document, policy).label,
        options: sortedBreadcrumbOptions(document.policies ?? [], (item) => ({
          href: policyHref(item.id),
          id: item.id,
          label: policyDisplayTitle(document, item).label
        }))
      } : { label: recordId === "new" ? "New policy" : recordId }
    ];
  }

  if (currentPage.key === "browser") {
    return [root];
  }

  if (currentPage.key === "shared") {
    return [{ label: "Profiles", href: "#/profiles" }, { label: "Shared Link" }];
  }

  return [root];
}

export function App({ repository }) {
  return <ApplicationDocumentProvider repository={repository}><PortfolioValueProvider><Application /></PortfolioValueProvider></ApplicationDocumentProvider>;
}

function Application() {
  const [currentPage, setCurrentPage] = useState(pageFromHash);
  const [quickSyncProfileId, setQuickSyncProfileId] = useState(null);
  const [syncConnection, setSyncConnection] = useState(null);
  const application = useApplicationDocument();
  const activeProfileId = application.profile?.id;
  const quickSyncProfile = profileById(application.profiles, quickSyncProfileId);
  const syncToken = syncTokenForProfile(syncConnection, activeProfileId);
  const syncReturnFocusRef = useRef(null);
  const routeKey = `${currentPage.key}:${currentPage.path.join("/")}`;
  const shellPage = shellPageForRoute(currentPage);
  useAppShortcuts(createAppShortcutBindings({ navigateToPage }));

  useEffect(() => {
    function handleHashChange() {
      setCurrentPage(pageFromHash());
    }

    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, []);

  useLayoutEffect(() => {
    if (!routeKey) return;
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [routeKey]);

  if (currentPage.key === "reports") {
    return <ReportBuilderPage />;
  }

  return (
    <AppShell
      application={application}
      breadcrumbs={breadcrumbsForPage(shellPage, application.document)}
      currentPage={shellPage}
      onSync={({ returnFocusTarget }) => {
        syncReturnFocusRef.current = returnFocusTarget;
        setQuickSyncProfileId(activeProfileId);
      }}
      pages={pages}
      syncEnabled={quickSyncAvailable(syncToken, quickSyncProfileId)}
    >
      <Fragment key={`${application.profile?.id ?? "loading"}:${currentPage.key}:${currentPage.path.join("/")}`}>
        {currentPage.key === "stylesheet" ? <StylesheetPage /> : null}
        {currentPage.key === "share-test" ? <ShareTestPage /> : null}
        {currentPage.key === "compliance" ? <CompliancePage policyId={decodedPathSegment(currentPage.path[0])} /> : null}
        {currentPage.key === "properties" ? currentPage.path[1]
          ? <PropertyValuePage propertyId={decodedPathSegment(currentPage.path[0])} valueId={decodedPathSegment(currentPage.path[1])} />
          : currentPage.path[0] ? <PropertyPage propertyId={decodedPathSegment(currentPage.path[0])} /> : <PropertiesPage /> : null}
        {currentPage.key === "assets" ? currentPage.path[0] ? <AssetPage assetId={decodedPathSegment(currentPage.path[0])} /> : <AssetsPage /> : null}
        {currentPage.key === "allocation" ? <AllocationPage /> : null}
        {currentPage.key === "browser" ? <BrowserPage viewId={currentPage.path[0]} /> : null}
        {currentPage.key === "policies" ? currentPage.path[0] ? <PolicyPage policyId={decodedPathSegment(currentPage.path[0])} /> : <PoliciesPage /> : null}
        {["profiles", "shared"].includes(currentPage.key) ? <RoutedProfilesPage application={application} currentPage={currentPage} onSyncConnectionChange={() => setSyncConnection(storedSyncConnection(activeProfileId))} /> : null}
        {currentPage.key === "settings" ? <SettingsPage onApplicationReset={() => { window.location.hash = "#/compliance"; }} /> : null}
        {currentPage.key === "help" ? <HelpPage /> : null}
        <FallbackPage currentPage={currentPage} />
      </Fragment>
      <QuickSyncDialog
        application={application}
        fetchRequest={fetch}
        key={quickSyncProfileId}
        onClose={() => setQuickSyncProfileId(null)}
        onSync={() => setSyncConnection(storedSyncConnection(activeProfileId))}
        profile={quickSyncProfile}
        returnFocusRef={syncReturnFocusRef}
        token={syncToken}
      />
    </AppShell>
  );
}

function FallbackPage({ currentPage }) {
  const implementedPages = ["stylesheet", "share-test", "shared", "compliance", "properties", "assets", "allocation", "browser", "policies", "profiles", "settings", "help"];
  if (implementedPages.includes(currentPage.key)) return null;
  return <PlaceholderPage page={currentPage} />;
}

function RoutedProfilesPage({ application, currentPage, onSyncConnectionChange }) {
  if (currentPage.key === "shared") return <SharedProfilePage onSyncConnectionChange={onSyncConnectionChange} ready={Boolean(application.profile)} routeValue={currentPage.path[0]} />;
  return <ProfilesPage onSyncConnectionChange={onSyncConnectionChange} />;
}
